export function adminOrgCsvPreview(args) {
  const {
    treeType,
    squads,
    currentUserEmail,
    activeImportId,
    teamUniqueness,
    minGroupSize,
    managerMembership,
    uniqueMembersCount,
    prunedTeams,
    shareableUrl,
  } = args;

  return {
    treeType: treeType,
    squads: squads || [],
    currentUserEmail: currentUserEmail || "",
    activeImportId: activeImportId || null,
    shareableUrl: shareableUrl || null,
    searchTerm: "",
    expandedTeamsIds: {},
    showPrunedTeamsModal: false,
    showTeamsList: false,

    // Settings that can be updated
    customSettings: {
      teamUniqueness: teamUniqueness || "teamNameParentTeamAndManagerEmail",
      minGroupSize: minGroupSize || 0,
      managerMembership: managerMembership || "none",
      prunedTeams: prunedTeams || [],
      missingTeams: [],
    },

    uniqueMembersCount: uniqueMembersCount || 0,

    init() {
      // Initialize employee email set

      // Sort squads in hierarchical order (parents before children)
      this.squads = this.sortSquadsHierarchically(this.squads);

      // Initialize squad expansion state
      this.squads.forEach((squad) => {
        if (squad.temp_id && squad.expanded) {
          this.expandedTeamsIds[squad.temp_id] = true;
        }
      });
    },

    get treeSquads() {
      let squads = this.squads;
      const searchTermPresent = this.searchTerm.length > 0;

      // Clear any highlighting if there's no search term
      if (!searchTermPresent) {
        squads.forEach((squad) => {
          squad.highlightedTeam = null;
          squad.highlightedReferenceId = null;
        });

        // If no search term, apply original filtering logic for expanded/collapsed state
        return squads?.filter((s) => {
          return (
            s.depth == 0 ||
            s.expanded == true ||
            this.expandedTeamsIds[s.parent_id] == true
          );
        });
      }

      return this.search(squads, this.searchTerm);
    },

    search(squads, searchTerm) {
      let previousDepth = -1;

      return squads
        .slice() // Create a copy to avoid modifying original array
        .reverse()
        .filter((squad) => {
          const teamName = (squad.team || "").toLowerCase();
          const mgrEmail = (squad.mgr_email || "").toLowerCase();
          const referenceId = (squad.reference_id || "").toLowerCase();
          const searchLower = searchTerm.toLowerCase();

          const teamMatchIndex = teamName.indexOf(searchLower);
          const referenceMatchIndex = referenceId.indexOf(searchLower);
          const isMatch =
            teamMatchIndex !== -1 ||
            mgrEmail.includes(searchLower) ||
            referenceMatchIndex !== -1;

          // Include if it's a parent of a match (has greater depth)
          const isParentOfMatch =
            previousDepth !== -1 &&
            squad.depth !== previousDepth &&
            squad.depth < previousDepth;

          delete squad.highlightedTeam;
          delete squad.highlightedReferenceId;

          if (isMatch || isParentOfMatch) {
            previousDepth = squad.depth;

            if (teamMatchIndex !== -1) {
              // Highlight team name matches
              const match = squad.team.substring(
                teamMatchIndex,
                teamMatchIndex + searchTerm.length,
              );
              squad.highlightedTeam = squad.team
                .split(match)
                .join(`<span class="bg-yellow-100">${match}</span>`);
            }

            if (referenceMatchIndex !== -1) {
              // Highlight reference ID matches
              const match = squad.reference_id.substring(
                referenceMatchIndex,
                referenceMatchIndex + searchTerm.length,
              );
              squad.highlightedReferenceId = squad.reference_id
                .split(match)
                .join(`<span class="bg-yellow-100">${match}</span>`);
            }

            return true;
          }

          return false;
        })
        .reverse();
    },

    changeTeamUniqueness(value) {
      this.customSettings.teamUniqueness = value;
      this.updateSettings();
    },

    changeMinGroupSize(value) {
      this.customSettings.minGroupSize = value;
      this.updateSettings();
    },

    changeManagerMembership(value) {
      this.customSettings.managerMembership = value;
      this.updateSettings();
    },

    updateSettings() {
      // Re-run staging import with new settings by calling stage_import
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `/admin/org-csvs/${this.activeImportId}/stage_import`;

      // Add CSRF token
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content");
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      // Add team_uniqueness parameter
      const teamUniquenessInput = document.createElement("input");
      teamUniquenessInput.type = "hidden";
      teamUniquenessInput.name = "team_uniqueness";
      teamUniquenessInput.value = this.customSettings.teamUniqueness;
      form.appendChild(teamUniquenessInput);

      // Add min_group_size parameter
      const minGroupSizeInput = document.createElement("input");
      minGroupSizeInput.type = "hidden";
      minGroupSizeInput.name = "min_group_size";
      minGroupSizeInput.value = this.customSettings.minGroupSize || 0;
      form.appendChild(minGroupSizeInput);

      // Add manager membership parameters based on radio button selection
      const includeManagersInput = document.createElement("input");
      includeManagersInput.type = "hidden";
      includeManagersInput.name = "include_managers_as_own_team_members";
      includeManagersInput.value =
        this.customSettings.managerMembership === "include";
      form.appendChild(includeManagersInput);

      const preventManagersInput = document.createElement("input");
      preventManagersInput.type = "hidden";
      preventManagersInput.name = "prevent_managers_as_own_team_members";
      preventManagersInput.value =
        this.customSettings.managerMembership === "prevent";
      form.appendChild(preventManagersInput);

      // Submit the form - this will trigger re-staging and redirect to processing page
      document.body.appendChild(form);
      form.submit();
    },

    missingTeams() {
      // Return any missing teams data passed from backend
      return this.customSettings.missingTeams || [];
    },

    sortSquadsHierarchically(squads) {
      if (!squads || squads.length === 0) return squads;

      // Create a map for quick lookup
      const squadMap = new Map();
      squads.forEach((squad) => {
        squadMap.set(squad.temp_id || squad.id, squad);
      });

      // Determine which teams are parents (have children)
      const squadIds = new Set(squads.map((s) => s.temp_id || s.id));
      const parentsSet = new Set();

      squads.forEach((squad) => {
        if (squad.parent_id && squadIds.has(squad.parent_id)) {
          parentsSet.add(squad.parent_id);
        }
      });

      const result = [];
      const processed = new Set();

      // Function to sort siblings according to precedence rules
      const sortSiblings = (siblings) => {
        const parents = [];
        const children = [];

        siblings.forEach((squad) => {
          const squadId = squad.temp_id || squad.id;
          if (parentsSet.has(squadId)) {
            parents.push(squad);
          } else {
            children.push(squad);
          }
        });

        // Sort parents alphabetically
        parents.sort((a, b) => (a.team || "").localeCompare(b.team || ""));

        // Sort children alphabetically
        children.sort((a, b) => (a.team || "").localeCompare(b.team || ""));

        // Return parents first, then children
        return [...parents, ...children];
      };

      // Recursive function to add squad and its children
      const addSquadAndChildren = (squad) => {
        if (!squad || processed.has(squad.temp_id || squad.id)) return;

        processed.add(squad.temp_id || squad.id);
        result.push(squad);

        // Find direct children
        const children = squads.filter(
          (s) =>
            s.parent_id &&
            (s.parent_id === squad.temp_id || s.parent_id === squad.id),
        );

        // Sort children according to precedence rules
        const sortedChildren = sortSiblings(children);

        // Recursively add each child and their descendants
        sortedChildren.forEach((child) => {
          addSquadAndChildren(child);
        });
      };

      // Start with root squads (depth 0 or no parent)
      const rootSquads = squads.filter(
        (squad) => squad.depth === 0 || !squad.parent_id,
      );

      // Sort root squads according to precedence rules
      const sortedRootSquads = sortSiblings(rootSquads);

      // Add each root squad and its descendants
      sortedRootSquads.forEach((rootSquad) => {
        addSquadAndChildren(rootSquad);
      });

      // Add any remaining squads that weren't processed (orphaned)
      squads.forEach((squad) => {
        if (!processed.has(squad.temp_id || squad.id)) {
          result.push(squad);
        }
      });

      return result;
    },

    toggleSquad(targetSquad) {
      // Function to recursively collapse all descendants
      const collapseDescendants = (parentId) => {
        this.squads.forEach((squad) => {
          if (squad.parent_id === parentId) {
            squad.expanded = false;
            delete this.expandedTeamsIds[squad.temp_id];
            // Recursively collapse children of this squad
            collapseDescendants(squad.temp_id);
          }
        });
      };

      this.squads = this.squads.map((squad) => {
        if (targetSquad.temp_id === squad.parent_id) {
          squad.parentExpanded = targetSquad.expanded;
        }
        if (targetSquad.temp_id === squad.temp_id) {
          targetSquad.expanded = squad.expanded = !squad.expanded;
          if (targetSquad.expanded) {
            this.expandedTeamsIds[targetSquad.temp_id] = true;
          } else {
            delete this.expandedTeamsIds[targetSquad.temp_id];
            // When collapsing, also collapse all descendants
            collapseDescendants(targetSquad.temp_id);
          }
        }
        return squad;
      });

      // Re-sort after toggle to maintain hierarchical order
      this.squads = this.sortSquadsHierarchically(this.squads);
    },

    expandAll() {
      this.squads.forEach((squad) => {
        squad.expanded = true;
        this.expandedTeamsIds[squad.temp_id] = true;
      });
    },

    collapseAll() {
      this.squads.forEach((squad) => {
        squad.expanded = false;
        delete this.expandedTeamsIds[squad.temp_id];
      });
    },

    async createShareablePreview() {
      if (this.shareableUrl) {
        return; // Already have a shareable URL, don't create another one
      }

      try {
        const response = await fetch(
          `/admin/org-csvs/${this.activeImportId}/create_shareable_preview`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document
                .querySelector('meta[name="csrf-token"]')
                .getAttribute("content"),
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            this.shareableUrl = data.url;
          }
        }
      } catch (error) {
        console.error("Failed to create shareable preview:", error);
      }
    },

    stageImport() {
      // Create a form and submit it to stage the import
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `/admin/org-csvs/${this.activeImportId}/stage_import`;

      // Add CSRF token
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content");
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      // Submit the form
      document.body.appendChild(form);
      form.submit();
    },

    submit(markAsPreview = false) {
      // Handle submission - could redirect to next step or submit for processing
      if (markAsPreview) {
        this.createShareablePreview();
      } else {
        this.stageImport();
      }
    },

    get shadowing() {
      return false; // Simplified for now
    },
  };
}
